import ArrowBack from '@mui/icons-material/ArrowBack'
import FolderOutlined from '@mui/icons-material/FolderOutlined'
import HomeOutlined from '@mui/icons-material/HomeOutlined'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FileRepositoryModel } from 'src/models/file-repository-model'
import { fetchFileRepositoryEntries } from 'src/service/fileRepositoryService'

interface MoveEntryModalProps {
  open: boolean
  entry: FileRepositoryModel | null
  onClose: () => void
  onMove: (id: string, targetParentId: string | null) => Promise<any>
}

interface BrowseState {
  parentId: string | null
  parentName: string
  directories: FileRepositoryModel[]
  loading: boolean
  history: Array<{ id: string | null; name: string }>
}

export function MoveEntryModal({
  open,
  entry,
  onClose,
  onMove,
}: MoveEntryModalProps) {
  const { t } = useTranslation()
  const [isMoving, setIsMoving] = useState(false)
  const [browseState, setBrowseState] = useState<BrowseState>({
    parentId: null,
    parentName: t('fileRepository.modal.move.root'),
    directories: [],
    loading: false,
    history: [],
  })

  const loadDirectories = useCallback(
    async (parentId: string | null) => {
      setBrowseState((prev) => ({ ...prev, loading: true }))
      const result = await fetchFileRepositoryEntries({
        parentId,
        type: 'DIRECTORY',
        limit: 100,
        sortBy: 'name',
        sortOrder: 'asc',
      })
      const dirs = (result?.data || []).filter((d) => d.id !== entry?.id)
      setBrowseState((prev) => ({
        ...prev,
        parentId,
        directories: dirs,
        loading: false,
      }))
    },
    [entry?.id]
  )

  useEffect(() => {
    if (open) {
      setBrowseState({
        parentId: null,
        parentName: t('fileRepository.modal.move.root'),
        directories: [],
        loading: false,
        history: [],
      })
      loadDirectories(null)
    }
  }, [open, loadDirectories, t])

  const navigateInto = (dir: FileRepositoryModel) => {
    setBrowseState((prev) => ({
      ...prev,
      history: [...prev.history, { id: prev.parentId, name: prev.parentName }],
      parentId: dir.id,
      parentName: dir.name,
    }))
    loadDirectories(dir.id)
  }

  const navigateBack = () => {
    setBrowseState((prev) => {
      const newHistory = [...prev.history]
      const previous = newHistory.pop()
      if (!previous) return prev
      loadDirectories(previous.id)
      return {
        ...prev,
        history: newHistory,
        parentId: previous.id,
        parentName: previous.name,
      }
    })
  }

  const handleMove = async () => {
    if (!entry) return
    setIsMoving(true)
    const res = await onMove(entry.id, browseState.parentId)
    setIsMoving(false)
    if (res) {
      onClose()
    }
  }

  const canGoBack = browseState.history.length > 0

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('fileRepository.modal.move.title')}</DialogTitle>
      <DialogContent>
        {/* Current location header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1,
            pb: 1,
            borderBottom: '1px solid rgba(0,0,0,0.12)',
          }}
        >
          {canGoBack && (
            <IconButton size="small" onClick={navigateBack}>
              <ArrowBack fontSize="small" />
            </IconButton>
          )}
          <HomeOutlined sx={{ color: '#FF5E1E', fontSize: 20 }} />
          <Typography variant="body2" fontWeight={500}>
            {browseState.parentName}
          </Typography>
        </Box>

        {/* Directory list */}
        {browseState.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : browseState.directories.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ py: 3, textAlign: 'center' }}
          >
            {t('fileRepository.empty.title')}
          </Typography>
        ) : (
          <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
            {browseState.directories.map((dir) => (
              <ListItemButton
                key={dir.id}
                onClick={() => navigateInto(dir)}
                sx={{
                  borderRadius: '4px',
                  ':hover': {
                    backgroundColor: 'rgba(255, 94, 30, 0.06)',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <FolderOutlined sx={{ color: '#FF5E1E' }} />
                </ListItemIcon>
                <ListItemText primary={dir.name} />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('general.button.cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleMove}
          disabled={isMoving}
        >
          {isMoving ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t('fileRepository.modal.move.button')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
